import { useEffect } from 'react'
import { Outlet, useLocation, useMatches } from 'react-router-dom'

import { ErrorBoundary } from './ErrorBoundary.js'
import { NavigationFrame } from './NavigationFrame.js'
import { useRange } from '../range/RangeProvider.js'

/**
 * The frame every screen sits in.
 *
 * The error boundary is inside the shell rather than around it, so a screen
 * that throws leaves the navigation intact and the user can move somewhere
 * else. A boundary at the root would replace the whole app with an error page
 * and leave no way out but a reload.
 */
export function AppShell() {
  /*
   * "All time" is bounded by the first and last entry, and those were read once
   * when the provider mounted — which is outside the router, so once per launch.
   *
   * Log something and the bounds are immediately stale: "all time" still ends
   * at whatever the last entry was when the app opened, so a chart of a
   * database with one fresh entry reported "0 of 1 days". Refreshing on every
   * navigation catches every writer with one hook rather than chasing the log
   * flow, the entry editor and the restore separately — it is two indexed reads.
   */
  const { refreshBounds } = useRange()
  const { pathname } = useLocation()
  useEffect(() => {
    void refreshBounds()
  }, [pathname, refreshBounds])

  // A route marked `handle: { sub: true }` is a sub-screen with no bottom nav.
  const sub = useMatches().some(
    (match) => (match.handle as { sub?: boolean } | undefined)?.sub === true,
  )

  return (
    <NavigationFrame sub={sub}>
      <ErrorBoundary>
        <Outlet />
      </ErrorBoundary>
    </NavigationFrame>
  )
}
